import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { util as sdkUtil } from '../util/sdkLoader';
import { denormalisedResponseEntities, ensureOwnListing } from '../util/data';
import * as log from '../util/log';
import { LISTING_STATE_DRAFT } from '../util/types';
import { storableError } from '../util/errors';
import { isUserAuthorized } from '../util/userHelpers';
import {
  getStatesNeedingProviderAttention,
  getStatesNeedingCustomerAttention,
} from '../transactions/transaction';

import {
  fetchNotifications as fetchNotificationsApi,
  fetchMembership as fetchMembershipApi,
  resendSignupConfirmation,
} from '../util/api';
import { authInfo } from './auth.duck';
import { updateStripeConnectAccount } from './stripeConnectAccount.duck';

// ================ Helper Functions ================ //

const mergeCurrentUser = (oldCurrentUser, newCurrentUser) => {
  const { id: oId, type: oType, attributes: oAttr, ...oldRelationships } = oldCurrentUser || {};
  const { id, type, attributes, ...relationships } = newCurrentUser || {};

  // Passing null will remove currentUser entity.
  // Only relationships are merged.
  // TODO figure out if sparse fields handling needs a better handling.
  return newCurrentUser === null
    ? null
    : oldCurrentUser === null
    ? newCurrentUser
    : { id, type, attributes, ...oldRelationships, ...relationships };
};

// ================ Async Thunks ================ //

//////////////////////////////////////////////////////////////////////
// Fetch ownListings to check if currentUser has published listings //
//////////////////////////////////////////////////////////////////////

const fetchCurrentUserHasListingsPayloadCreator = (_, thunkAPI) => {
  const { getState, extra: sdk, rejectWithValue } = thunkAPI;
  const { currentUser } = getState().user;

  if (!currentUser) {
    return Promise.resolve({ hasListings: false });
  }

  const params = {
    // Since we are only interested in if the user has published
    // listings, we only need at most one result.
    states: 'published',
    page: 1,
    perPage: 1,
  };

  return sdk.ownListings
    .query(params)
    .then(response => {
      const hasListings = response.data.data && response.data.data.length > 0;

      const hasPublishedListings =
        hasListings &&
        ensureOwnListing(response.data.data[0]).attributes.state !== LISTING_STATE_DRAFT;
      return { hasListings: !!hasPublishedListings };
    })
    .catch(e => rejectWithValue(storableError(e)));
};

export const fetchCurrentUserHasListingsThunk = createAsyncThunk(
  'user/fetchCurrentUserHasListings',
  fetchCurrentUserHasListingsPayloadCreator
);

export const fetchCurrentUserHasListings = () => dispatch => {
  return dispatch(fetchCurrentUserHasListingsThunk());
};

///////////////////////////////////////////////////////////
// Fetch transactions to check if currentUser has orders //
///////////////////////////////////////////////////////////

const fetchCurrentUserHasOrdersPayloadCreator = (_, { getState, extra: sdk, rejectWithValue }) => {
  if (!getState().user.currentUser) {
    return Promise.resolve({ hasOrders: false });
  }

  const params = {
    only: 'order',
    page: 1,
    perPage: 1,
  };

  return sdk.transactions
    .query(params)
    .then(response => {
      const hasOrders = response.data.data && response.data.data.length > 0;
      return { hasOrders: !!hasOrders };
    })
    .catch(e => rejectWithValue(storableError(e)));
};

export const fetchCurrentUserHasOrdersThunk = createAsyncThunk(
  'user/fetchCurrentUserHasOrders',
  fetchCurrentUserHasOrdersPayloadCreator
);

export const fetchCurrentUserHasOrders = () => dispatch => {
  return dispatch(fetchCurrentUserHasOrdersThunk());
};

/////////////////////////////////////////////////////////////////////////////////////
// Fetch transactions in specific states to check if currentUser has notifications //
/////////////////////////////////////////////////////////////////////////////////////

// Notificaiton page size is max (100 items on page)
const NOTIFICATION_PAGE_SIZE = 100;

const fetchCurrentUserNotificationsPayloadCreator = (_, { extra: sdk, rejectWithValue }) => {
  const statesNeedingProviderAttention = getStatesNeedingProviderAttention() || [];
  const statesNeedingCustomerAttention = getStatesNeedingCustomerAttention() || [];

  const paramsForSales = {
    only: 'sale',
    states: statesNeedingProviderAttention.map(state => `state/${state}`).join(','),
    page: 1,
    perPage: NOTIFICATION_PAGE_SIZE,
  };
  const paramsForOrders = {
    only: 'order',
    states: statesNeedingCustomerAttention.map(state => `state/${state}`).join(','),
    page: 1,
    perPage: NOTIFICATION_PAGE_SIZE,
  };

  return Promise.all([
    sdk.transactions.query(paramsForSales),
    sdk.transactions.query(paramsForOrders),
  ])
    .then(([sales, orders]) => {
      const saleNotificationsCount = sales.data.data.length;
      const orderNotificationsCount = orders.data.data.length;
      return { saleNotificationsCount, orderNotificationsCount };
    })
    .catch(e => rejectWithValue(storableError(e)));
};

export const fetchCurrentUserNotificationsThunk = createAsyncThunk(
  'user/fetchCurrentUserNotifications',
  fetchCurrentUserNotificationsPayloadCreator
);

export const fetchCurrentUserNotifications = () => dispatch => {
  return dispatch(fetchCurrentUserNotificationsThunk());
};

// Fetch unread transaction IDs from the notifications endpoint
const fetchUnreadNotificationsPayloadCreator = async (_, { rejectWithValue }) => {
  try {
    const response = await fetchNotificationsApi();
    return response;
  } catch (e) {
    return rejectWithValue(storableError(e));
  }
};

export const fetchUnreadNotificationsThunk = createAsyncThunk(
  'user/fetchUnreadNotifications',
  fetchUnreadNotificationsPayloadCreator
);

export const fetchUnreadNotifications = () => dispatch =>
  dispatch(fetchUnreadNotificationsThunk());

// Sibling thunk to fetchCurrentUser. Stripe stays source of truth — this
// just hydrates state.user.currentUserMembership for selectors.
const fetchCurrentUserMembershipPayloadCreator = async (_, { rejectWithValue }) => {
  try {
    return await fetchMembershipApi();
  } catch (e) {
    return rejectWithValue(storableError(e));
  }
};

export const fetchCurrentUserMembershipThunk = createAsyncThunk(
  'user/fetchCurrentUserMembership',
  fetchCurrentUserMembershipPayloadCreator
);

export const fetchCurrentUserMembership = () => dispatch =>
  dispatch(fetchCurrentUserMembershipThunk());

const fetchCurrentUserPayloadCreator = (options, thunkAPI) => {
  const { getState, dispatch, extra: sdk, rejectWithValue } = thunkAPI;
  const state = getState();
  const { currentUserHasListings, currentUserShowTimestamp } = state.user || {};
  const { isAuthenticated } = state.auth;
  const {
    callParams = null,
    updateHasListings = true,
    updateNotifications = true,
    afterLogin,
    enforce = false, // Automatic emailVerification might be called too fast
  } = options || {};

  // Double fetch might happen when e.g. profile page is making a full page load
  const aSecondAgo = new Date().getTime() - 1000;
  if (!enforce && currentUserShowTimestamp > aSecondAgo) {
    return Promise.resolve(state.user.currentUser);
  }

  if (!isAuthenticated && !afterLogin) {
    // Make sure current user is null
    return Promise.resolve(null);
  }

  const parameters = callParams || {
    include: ['effectivePermissionSet', 'profileImage', 'stripeAccount'],
    'fields.image': [
      'variants.square-small',
      'variants.square-small2x',
      'variants.square-xsmall',
      'variants.square-xsmall2x',
    ],
    'imageVariant.square-xsmall': sdkUtil.objectQueryString({
      w: 40,
      h: 40,
      fit: 'crop',
    }),
    'imageVariant.square-xsmall2x': sdkUtil.objectQueryString({
      w: 80,
      h: 80,
      fit: 'crop',
    }),
  };

  return sdk.currentUser
    .show(parameters)
    .then(response => {
      const entities = denormalisedResponseEntities(response);
      if (entities.length !== 1) {
        throw new Error('Expected a resource in the sdk.currentUser.show response');
      }
      const currentUser = entities[0];

      // Save stripeAccount to store.stripe.stripeAccount if it exists
      if (currentUser.stripeAccount) {
        dispatch(updateStripeConnectAccount(currentUser.stripeAccount));
      }

      // set current user id to the logger
      log.setUserId(currentUser.id.uuid);
      return currentUser;
    })
    .then(currentUser => {
      // If currentUser is not active (e.g. in 'pending-approval' state),
      // then they don't have listings or transactions that we care about.
      if (isUserAuthorized(currentUser)) {
        if (currentUserHasListings === false && updateHasListings !== false) {
          dispatch(fetchCurrentUserHasListings());
        }

        if (updateNotifications !== false) {
          dispatch(fetchCurrentUserNotifications());
          dispatch(fetchUnreadNotifications());
        }

        if (!currentUser.attributes.emailVerified) {
          dispatch(fetchCurrentUserHasOrders());
        }

        dispatch(fetchCurrentUserMembership());
      }

      // Make sure auth info is up to date
      dispatch(authInfo());
      return currentUser;
    })
    .catch(e => {
      // Make sure auth info is up to date
      dispatch(authInfo());
      log.error(e, 'fetch-current-user-failed');
      return rejectWithValue(storableError(e));
    });
};

export const fetchCurrentUserThunk = createAsyncThunk(
  'user/fetchCurrentUser',
  fetchCurrentUserPayloadCreator
);
// Backward compatible wrapper for the thunk
/**
 * Fetch currentUser API entity.
 *
 * @param {Object} options
 * @param {Object} [options.callParams]           Optional parameters for the currentUser.show().
 * @param {boolean} [options.updateHasListings]   Make extra call for fetchCurrentUserHasListings()?
 * @param {boolean} [options.updateNotifications] Make extra call for fetchCurrentUserNotifications()?
 * @param {boolean} [options.afterLogin]          Fetch is no-op for unauthenticated users except after login() call
 * @param {boolean} [options.enforce]             Enforce the call even if the currentUser entity is freshly fetched.
 */
export const fetchCurrentUser = options => dispatch => {
  return dispatch(fetchCurrentUserThunk(options));
};

/////////////////////////////////////////////
// Send verification email to currentUser //
/////////////////////////////////////////////

/**
 * Re-issue the Supabase signup-confirmation email. Resend is email-based
 * (Supabase's public resend endpoint), so an address is required: it is passed
 * explicitly when the caller knows it, otherwise taken from the loaded
 * currentUser. It is never read from a Supabase session, since an unconfirmed
 * account has no session (and even a loaded Sharetribe currentUser may not have
 * a live Supabase one). IdP-bridged Sharetribe users have no usable
 * Sharetribe-side verification email, so the BFF proxies Supabase.
 */
const sendVerificationEmailPayloadCreator = async (email, { getState, rejectWithValue }) => {
  try {
    const address = email || getState()?.user?.currentUser?.attributes?.email;
    await resendSignupConfirmation(address);
    return {};
  } catch (e) {
    return rejectWithValue(storableError(e));
  }
};
export const sendVerificationEmailThunk = createAsyncThunk(
  'user/sendVerificationEmail',
  sendVerificationEmailPayloadCreator,
  {
    condition: (_, { getState }) => {
      return !getState()?.user?.sendVerificationEmailInProgress;
    },
  }
);

export const sendVerificationEmail = email => dispatch => {
  return dispatch(sendVerificationEmailThunk(email));
};

// ================ Slice ================ //

const userSlice = createSlice({
  name: 'user',
  initialState: {
    currentUser: null,
    currentUserShowTimestamp: 0,
    currentUserShowError: null,
    currentUserHasListings: false,
    currentUserHasListingsError: null,
    currentUserSaleNotificationCount: 0,
    currentUserOrderNotificationCount: 0,
    currentUserNotificationCountError: null,
    currentUserHasOrders: null, // This is not fetched unless unverified emails exist
    currentUserHasOrdersError: null,
    unreadSaleTransactionIds: [],
    unreadOrderTransactionIds: [],
    unreadNotificationsError: null,
    sendVerificationEmailInProgress: false,
    sendVerificationEmailError: null,
    currentUserMembership: null,
    currentUserMembershipError: null,
  },
  reducers: {
    clearCurrentUser: state => {
      state.currentUser = null;
      state.currentUserShowError = null;
      state.currentUserHasListings = false;
      state.currentUserHasListingsError = null;
      state.currentUserSaleNotificationCount = 0;
      state.currentUserOrderNotificationCount = 0;

      state.currentUserNotificationCountError = null;
      state.unreadSaleTransactionIds = [];
      state.unreadOrderTransactionIds = [];
      state.unreadNotificationsError = null;
      state.currentUserMembership = null;
      state.currentUserMembershipError = null;
    },
    setCurrentUser: (state, action) => {
      state.currentUser = mergeCurrentUser(state.currentUser, action.payload);
    },
    setCurrentUserHasOrders: state => {
      state.currentUserHasOrders = true;
    },
    removeUnreadTransaction: (state, action) => {
      const txId = action.payload;
      state.unreadSaleTransactionIds = state.unreadSaleTransactionIds.filter(id => id !== txId);
      state.unreadOrderTransactionIds = state.unreadOrderTransactionIds.filter(id => id !== txId);
    },
  },
  extraReducers: builder => {
    builder
      // fetchCurrentUser
      .addCase(fetchCurrentUserThunk.pending, state => {
        state.currentUserShowError = null;
      })
      .addCase(fetchCurrentUserThunk.fulfilled, (state, action) => {
        state.currentUser = mergeCurrentUser(state.currentUser, action.payload);
        state.currentUserShowTimestamp = action.payload ? new Date().getTime() : 0;
      })
      .addCase(fetchCurrentUserThunk.rejected, (state, action) => {
        console.error(action.payload);
        state.currentUserShowError = action.payload;
      })
      // fetchCurrentUserHasListings
      .addCase(fetchCurrentUserHasListingsThunk.pending, state => {
        state.currentUserHasListingsError = null;
      })
      .addCase(fetchCurrentUserHasListingsThunk.fulfilled, (state, action) => {
        state.currentUserHasListings = action.payload.hasListings;
      })
      .addCase(fetchCurrentUserHasListingsThunk.rejected, (state, action) => {
        console.error(action.payload);
        state.currentUserHasListingsError = action.payload;
      })
      // fetchCurrentUserNotifications
      .addCase(fetchCurrentUserNotificationsThunk.pending, state => {
        state.currentUserNotificationCountError = null;
      })
      .addCase(fetchCurrentUserNotificationsThunk.fulfilled, (state, action) => {
        state.currentUserSaleNotificationCount = action.payload.saleNotificationsCount;
        state.currentUserOrderNotificationCount = action.payload.orderNotificationsCount;
      })
      .addCase(fetchCurrentUserNotificationsThunk.rejected, (state, action) => {
        console.error(action.payload);
        state.currentUserNotificationCountError = action.payload;
      })
      // fetchUnreadNotifications
      .addCase(fetchUnreadNotificationsThunk.pending, state => {
        state.unreadNotificationsError = null;
      })
      .addCase(fetchUnreadNotificationsThunk.fulfilled, (state, action) => {
        state.unreadSaleTransactionIds = action.payload.sales || [];
        state.unreadOrderTransactionIds = action.payload.orders || [];
      })
      .addCase(fetchUnreadNotificationsThunk.rejected, (state, action) => {
        console.error(action.payload); // eslint-disable-line
        state.unreadNotificationsError = action.payload;
      })
      // fetchCurrentUserHasOrders
      .addCase(fetchCurrentUserHasOrdersThunk.pending, state => {
        state.currentUserHasOrdersError = null;
      })
      .addCase(fetchCurrentUserHasOrdersThunk.fulfilled, (state, action) => {
        state.currentUserHasOrders = action.payload.hasOrders;
      })
      .addCase(fetchCurrentUserHasOrdersThunk.rejected, (state, action) => {
        console.error(action.payload);
        state.currentUserHasOrdersError = action.payload;
      })
      // fetchCurrentUserMembership
      .addCase(fetchCurrentUserMembershipThunk.pending, state => {
        state.currentUserMembershipError = null;
      })
      .addCase(fetchCurrentUserMembershipThunk.fulfilled, (state, action) => {
        state.currentUserMembership = action.payload;
      })
      .addCase(fetchCurrentUserMembershipThunk.rejected, (state, action) => {
        console.error(action.payload); // eslint-disable-line
        state.currentUserMembershipError = action.payload;
      })
      // sendVerificationEmail
      .addCase(sendVerificationEmailThunk.pending, state => {
        state.sendVerificationEmailInProgress = true;
        state.sendVerificationEmailError = null;
      })
      .addCase(sendVerificationEmailThunk.fulfilled, state => {
        state.sendVerificationEmailInProgress = false;
      })
      .addCase(sendVerificationEmailThunk.rejected, (state, action) => {
        state.sendVerificationEmailInProgress = false;
        state.sendVerificationEmailError = action.payload;
      });
  },
});

export default userSlice.reducer;

export const {
  clearCurrentUser,
  removeUnreadTransaction,
  setCurrentUser,
  setCurrentUserHasOrders,
} = userSlice.actions;

// ================ Selectors ================ //

export const selectUnreadSaleIds = state => state.user.unreadSaleTransactionIds;
export const selectUnreadOrderIds = state => state.user.unreadOrderTransactionIds;
export const selectSaleNotificationCount = state => state.user.unreadSaleTransactionIds.length;
export const selectOrderNotificationCount = state => state.user.unreadOrderTransactionIds.length;

export const selectMembership = state => state.user.currentUserMembership;
export const selectMembershipSubscription = state =>
  state.user.currentUserMembership?.subscription ?? null;
export const selectMembershipTierName = state =>
  state.user.currentUserMembership?.subscription?.tier ?? null;
export const selectMembershipTierNumber = state =>
  state.user.currentUserMembership?.subscription?.membershipTier ?? 0;
export const selectMembershipQuota = state =>
  state.user.currentUserMembership?.quota ?? { used: 0, limit: null, periodStart: null };
export const selectMembershipBoostCredits = state =>
  state.user.currentUserMembership?.boostCredits ?? {
    granted: 0,
    consumed: 0,
    remaining: 0,
    resetsAt: null,
  };
export const selectMembershipHeldRewards = state =>
  state.user.currentUserMembership?.heldRewards ?? [];
export const selectMembershipPrices = state =>
  state.user.currentUserMembership?.prices ?? [];
export const selectMembershipOnboardingDismissed = state =>
  state.user.currentUserMembership?.onboardingDismissedAt ?? null;
export const selectHasActiveMembership = state =>
  !!state.user.currentUserMembership?.subscription;

export const hasCurrentUserErrors = state => {
  const { user } = state;
  return (
    user.currentUserShowError ||
    user.currentUserHasListingsError ||
    user.currentUserNotificationCountError ||
    user.currentUserHasOrdersError
  );
};
